import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EmbeddingDialogComponent } from '../../dialogs/embedding-dialog/embedding-dialog.component';
import { MoodleConnectDialogComponent } from '../../dialogs/moodle-connect-dialog/moodle-connect-dialog.component';
import { LtiConnectDialogComponent } from '../../dialogs/lti-connect-dialog/lti-connect-dialog.component';
import { IliasConnectDialogComponent } from '../../dialogs/ilias-connect-dialog/ilias-connect-dialog.component';
import { IliasOverviewService } from '../../../services/ilias-overview.service';
import { CloudConnectDialogComponent } from '../../dialogs/cloud-connect-dialog/cloud-connect-dialog.component';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { IntegrationService } from '../../../services/integration.service';
import { LmsRegistration, LmsRegistryService } from '../../../services/lms-registry.service';

const CLOUD_PROVIDERS = ['google_drive', 'dropbox', 'sharepoint'];

@Component({
  selector: 'app-integration-card',
  templateUrl: './integration-card.component.html',
  styleUrl: './integration-card.component.scss',
})
export class IntegrationCardComponent implements OnInit {
  @Input() data: any;
  moodleConnected = false;
  moodlePaused = false;
  /** Anzahl aktiv verbundener LTI-Verbindungen — treibt den „Verbunden"-Chip
   *  auf der LTI-Kachel, analog zu `moodleConnected`. */
  ltiConnectedCount = 0;
  /** Eine ILIAS-Verbindung besteht. Die Kachel zeigt dann denselben Chip wie
   *  Moodle Sync — sie ist das Gegenstück dazu, nicht zur LTI-Kachel. */
  iliasConnected = false;
  /** Zusätzlich läuft der automatische Abgleich. Ohne den ist die Verbindung
   *  zwar da, es fließen aber keine Daten — und genau das soll man sehen. */
  iliasSyncEnabled = false;
  cloudConnected = false;
  cloudEmail: string | null = null;
  testing = false;

  get isConnected(): boolean {
    return (
      this.moodleConnected ||
      this.moodlePaused ||
      this.cloudConnected ||
      this.ltiConnectedCount > 0 ||
      this.iliasConnected
    );
  }

  /** Verbunden, aber ohne laufenden Abgleich — als eigener Zustand sichtbar,
   *  weil „verbunden" sonst mehr verspricht, als die Kachel hält. */
  get isIdleSync(): boolean {
    return this.data?.link === 'ilias' && this.iliasConnected && !this.iliasSyncEnabled;
  }

  get isPaused(): boolean {
    return this.data?.link === 'moodle' && this.moodlePaused;
  }

  get statusLabelKey(): string {
    if (this.isPaused) return 'components.integrationCard.paused';
    if (this.isIdleSync) return 'components.integrationCard.syncOff';
    return 'components.integrationCard.connected';
  }

  get isCloudProvider(): boolean {
    return CLOUD_PROVIDERS.includes(this.data?.link);
  }

  constructor(
    protected dialogService: NbDialogService,
    private integrationService: IntegrationService,
    private lmsRegistry: LmsRegistryService,
    private toastr: NbToastrService,
    private t: TranslateService,
    private router: Router,
    private iliasOverview: IliasOverviewService,
  ) {}

  ngOnInit() {
    if (this.data?.link === 'moodle') {
      this.refreshMoodleState();
    }

    if (this.data?.link === 'lti') {
      this.refreshLtiState();
    }

    if (this.data?.link === 'ilias') {
      this.refreshIliasState();
    }

    if (CLOUD_PROVIDERS.includes(this.data?.link)) {
      this.integrationService.getCloudStatus(this.data.link, true).subscribe({
        next: (status) => {
          this.cloudConnected = status.connected;
          this.cloudEmail = status.account_email || null;
          if (!status.connected && status.status === 'error') {
            this.promptReconnect();
          }
        },
        error: () => {
          this.cloudConnected = false;
        },
      });
    }
  }

  /** Eine Verbindung zählt als verbunden, sobald sie einen erfolgreichen
   *  Launch hatte oder vollständig eingerichtet ist. Pausierte und getrennte
   *  bleiben absichtlich außen vor — sie beantworten keine Launches. */
  private refreshLtiState(): void {
    this.lmsRegistry.listRegistrations('lti13').pipe(
      catchError(() => of([] as LmsRegistration[])),
    ).subscribe((rows) => {
      this.ltiConnectedCount = rows.filter(
        (r) => r.connection_status === 'connected' || r.connection_status === 'ready',
      ).length;
    });
  }

  /** Der Endpunkt verlangt eine privilegierte Rolle. Ein Dozent bekommt 403
   *  und damit schlicht keinen Chip — die Kachel bleibt bedienbar. */
  private refreshIliasState(): void {
    this.iliasOverview.listConnections().pipe(
      catchError(() => of([])),
    ).subscribe((rows: any[]) => {
      // Eine Zeile allein heisst nicht verbunden: getrennte und pausierte
      // stehen dort genauso. Vorher zeigte die Kachel „verbunden", solange
      // ueberhaupt je eine ILIAS-Verbindung angelegt worden war.
      const live = rows.filter(
        (r) => !r.paused && r.connection_status !== 'disconnected',
      );
      this.iliasConnected = live.length > 0;
      // „Abgleich laeuft" hing an `auto_sync.enabled` — dem Schalter fuer
      // Stammdaten, den es nicht mehr gibt: Kurse, Mitglieder und Lehrende
      // werden immer gelesen. Gemeint ist hier, ob ueberhaupt ein Datenweg
      // besteht: gekoppeltes Plugin oder hinterlegter SOAP-Zugang.
      this.iliasSyncEnabled = live.some(
        (r) => !!r.plugin?.paired || !!r.soap_configured,
      );
    });
  }

  private refreshMoodleState(): void {
    this.lmsRegistry.listMoodleSites().pipe(
      catchError(() => of([] as LmsRegistration[])),
    ).subscribe({
      next: (moodleSites) => {
        const primarySite =
          moodleSites.find((site) => site.is_primary && this.isTokenReadyMoodleSite(site)) ||
          moodleSites.find((site) => this.isTokenReadyMoodleSite(site)) ||
          moodleSites.find((site) => site.is_primary && !this.isDisconnectedMoodleSite(site)) ||
          moodleSites.find((site) => !this.isDisconnectedMoodleSite(site)) ||
          null;

        this.moodlePaused = !!primarySite && this.isPausedMoodleSite(primarySite);
        this.moodleConnected =
          !!primarySite &&
          this.isTokenReadyMoodleSite(primarySite) &&
          !this.moodlePaused;
      },
      error: () => {
        this.moodleConnected = false;
        this.moodlePaused = false;
      },
    });
  }

  private effectiveMoodleStatus(site: LmsRegistration): string {
    const healthStatus = String(site.health_status || '').toLowerCase();
    if (healthStatus && !['ok', 'unknown'].includes(healthStatus)) {
      return healthStatus;
    }
    return String(site.connection_status || 'unknown').toLowerCase();
  }

  private isDisconnectedMoodleSite(site: LmsRegistration): boolean {
    return ['disconnected', 'superseded', 'token_revoked'].includes(
      this.effectiveMoodleStatus(site),
    );
  }

  private isPausedMoodleSite(site: LmsRegistration): boolean {
    return !!site.disabled_at || this.effectiveMoodleStatus(site).startsWith('paused');
  }

  private isTokenReadyMoodleSite(site: LmsRegistration): boolean {
    return (
      ['connected', 'token_ready', 'ok'].includes(this.effectiveMoodleStatus(site)) &&
      !!site.has_ws_token &&
      !site.ws_token_revoked_at
    );
  }

  navigate(link: string) {
    if (link === 'webbrowser') {
      this.dialogService.open(EmbeddingDialogComponent, {
        context: {
          botId: undefined,
        },
      });
      return;
    }

    if (link === 'moodle') {
      this.dialogService.open(MoodleConnectDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: true,
      }).onClose.subscribe(() => this.refreshMoodleState());
      return;
    }

    if (link === 'lti') {
      // Dialog statt Route: Verbindungen anlegen, Status sehen und trennen
      // passiert alles hier — dieselbe Form wie bei der Moodle-Kachel. Die
      // Platzierungen je Verbindung liegen weiterhin auf /tutors/placements.
      this.dialogService.open(LtiConnectDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: true,
      }).onClose.subscribe(() => this.refreshLtiState());
      return;
    }

    if (link === 'ilias') {
      // Dieselbe Form wie bei Moodle: die Kachel regelt die Verbindung, die
      // Daten liegen unter LMS → ILIAS. Verbunden wird ILIAS weiterhin über
      // die LTI-Kachel; hier kommen Zugang und Abgleich dazu.
      this.dialogService.open(IliasConnectDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: true,
      }).onClose.subscribe((result) => {
        this.refreshIliasState();
        // Ohne Verbindung gibt es im ILIAS-Dialog nichts einzustellen; der Weg
        // dorthin führt über die LTI-Kachel. Statt den Admin dort selbst
        // suchen zu lassen, reicht der Dialog weiter — sonst endet der
        // Leerzustand in einem „Schließen" und die Frage bleibt offen.
        if (result === 'open-lti') {
          this.dialogService.open(LtiConnectDialogComponent, {
            hasScroll: true,
            closeOnBackdropClick: true,
          }).onClose.subscribe(() => {
            this.refreshLtiState();
            this.refreshIliasState();
          });
        }
      });
      return;
    }

    if (CLOUD_PROVIDERS.includes(link)) {
      this.dialogService.open(CloudConnectDialogComponent, {
        context: { provider: link },
        hasScroll: true,
        closeOnBackdropClick: true,
      }).onClose.subscribe((changed: boolean) => {
        if (changed) {
          this.integrationService.getCloudStatus(link).subscribe({
            next: (status) => {
              this.cloudConnected = status.connected;
              this.cloudEmail = status.account_email || null;
            },
          });
        }
      });
      return;
    }

    if (link) {
      window.open(link, '_blank');
    }
  }

  testConnection(event: Event) {
    event.stopPropagation();
    if (!this.isCloudProvider || this.testing) return;

    this.testing = true;
    this.integrationService.testCloudConnection(this.data.link).subscribe({
      next: (status) => {
        this.testing = false;
        if (status.connected) {
          this.cloudConnected = true;
          this.cloudEmail = status.account_email || null;
          this.toastr.success(
            this.t.instant('components.integrationCard.testSuccess'),
            '',
          );
        } else {
          this.cloudConnected = false;
          this.cloudEmail = null;
          this.promptReconnect();
        }
      },
      error: () => {
        this.testing = false;
        this.cloudConnected = false;
        this.cloudEmail = null;
        this.promptReconnect();
      },
    });
  }

  private promptReconnect() {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        title: 'components.integrationCard.reconnectTitle',
        message: 'components.integrationCard.reconnectMessage',
      },
    });

    ref.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.navigate(this.data.link);
      }
    });
  }
}
